// client/src/components/contratos/ContratoPreview.js
// Preview/impressão do contrato — usa `contrato.conteudoHtml`, o snapshot já
// processado e congelado no momento da criação (ver
// server/controllers/contratosController.js createContrato +
// server/utils/contractUtils.js populateContractTemplate). Não refaz fetch
// do template nem reprocessa placeholders no client — o HTML final já vem
// pronto da API, isso é o que garante que o preview mostra exatamente o que
// foi gerado (e não diverge do texto realmente salvo no contrato).
import React, { useState, useEffect } from 'react';
import { FileText, Download, Printer, Eye, EyeOff, AlertCircle, ChevronLeft, ChevronRight, ArrowLeft, XCircle, History, FileDown, Loader2 } from 'lucide-react';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency as formatCurrencyUtil } from '../../utils/formatters';
import { useVersoesContrato } from '../../hooks/useContratos';
import useAlert from '../../hooks/useAlert';

const getContraparteNome = (contrato) => {
    const c = contrato?.cliente;
    if (!c || typeof c !== 'object') return 'N/A';
    if (contrato.modeloContraparte === 'Fornecedor') return c.nomeFantasia || c.razaoSocial || 'N/A';
    if (contrato.modeloContraparte === 'Transportador') return c.nomeCompleto || c.razaoSocial || c.nomeFantasia || 'N/A';
    return c.user?.name || c.nome || 'N/A';
};

// Divide o HTML em "páginas" A4 por heurística de tamanho, respeitando
// headings (h1-h6) como pontos de quebra preferenciais.
const splitContentIntoPages = (content) => {
    if (!content) return [''];

    const sections = content.split(/(<h[1-6][^>]*>.*?<\/h[1-6]>)/gi);
    const pages = [];
    let currentPage = '';
    let currentPageSize = 0;
    const maxPageSize = 2500;

    sections.forEach(section => {
        const sectionSize = section.length;
        if (currentPageSize + sectionSize > maxPageSize && currentPage) {
            pages.push(currentPage);
            currentPage = section;
            currentPageSize = sectionSize;
        } else {
            currentPage += section;
            currentPageSize += sectionSize;
        }
    });

    if (currentPage) pages.push(currentPage);
    return pages;
};

const PAGE_STYLE = `
    @page { size: A4; margin: 2cm; }
    body { font-family: Arial, Helvetica, sans-serif; color: #222; line-height: 1.5; margin: 0; padding: 2cm; background: white; min-height: 297mm; box-sizing: border-box; }
    header { border-bottom: 2px solid #e6e9ee; padding-bottom: 10px; margin-bottom: 20px; }
    h1 { font-size: 20px; color: #0b63d6; margin: 0; }
    .meta { font-size: 14px; color: #555; }
    section { margin-bottom: 14px; }
    .clause { margin: 8px 0; padding-left: 10px; }
    footer { margin-top: 30px; }
    .sign { margin-top: 50px; display: flex; justify-content: space-between; }
    .sig-block { width: 45%; text-align: center; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #f8f9fa; }
    .page-number { position: fixed; bottom: 1cm; right: 2cm; font-size: 12px; color: #666; }
`;

const ContratoPreview = ({ contrato, onClose }) => {
    const { setAlert } = useAlert();
    const { data: versoes = [], isLoading: isLoadingVersoes } = useVersoesContrato(contrato?._id);
    // ✅ ACHADO: fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const formatCurrency = (value) => formatCurrencyUtil(value || 0, currency, locale);
    const [showPreview, setShowPreview] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageContents, setPageContents] = useState([]);

    useEffect(() => {
        const pages = splitContentIntoPages(contrato?.conteudoHtml);
        setPageContents(pages);
        setCurrentPage(1);
    }, [contrato]);

    const totalPages = pageContents.length;

    const createPageContent = (pageNumber) => {
        const pageContent = pageContents[pageNumber - 1] || '';
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Contrato ${contrato.numeroContrato} - Página ${pageNumber}</title>
                <style>${PAGE_STYLE}</style>
            </head>
            <body>
                ${pageContent}
                <div class="page-number">Página ${pageNumber} de ${totalPages}</div>
            </body>
            </html>
        `;
    };

    const createPrintContent = () => {
        const pagesHTML = pageContents.map((content, index) => `
            <div class="a4-page" style="page-break-after: ${index < pageContents.length - 1 ? 'always' : 'auto'};">
                <div class="page-content">
                    ${content}
                    <div class="page-number">Página ${index + 1} de ${pageContents.length}</div>
                </div>
            </div>
        `).join('');

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Contrato ${contrato.numeroContrato}</title>
                <style>
                    @page { size: A4; margin: 2cm; }
                    body { font-family: Arial, Helvetica, sans-serif; color: #222; line-height: 1.5; margin: 0; padding: 0; background: white; }
                    .a4-page { width: 210mm; min-height: 297mm; padding: 2cm; background: white; box-sizing: border-box; position: relative; }
                    .page-content { height: 100%; }
                    header { border-bottom: 2px solid #e6e9ee; padding-bottom: 10px; margin-bottom: 20px; }
                    h1 { font-size: 20px; color: #0b63d6; margin: 0; }
                    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f8f9fa; }
                    .page-number { position: absolute; bottom: 1cm; right: 2cm; font-size: 12px; color: #666; }
                    @media print {
                        body { margin: 0; padding: 0; background: white; }
                        .a4-page { width: 210mm; height: 297mm; margin: 0; padding: 2cm; page-break-after: always; }
                        .a4-page:last-child { page-break-after: auto; }
                    }
                </style>
            </head>
            <body>${pagesHTML}</body>
            </html>
        `;
    };

    const handlePrint = () => {
        const printContent = createPrintContent();
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            setAlert('Não foi possível abrir a janela de impressão. Verifique se o pop-up está bloqueado.', 'warning');
            return;
        }
        printWindow.document.write(printContent);
        printWindow.document.close();
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
        }, 500);
    };

    const handleDownload = () => {
        const content = createPrintContent();
        const blob = new Blob([content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contrato-${contrato.numeroContrato}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const togglePreview = () => setShowPreview(prev => !prev);
    const nextPage = () => setCurrentPage(p => Math.min(p + 1, totalPages));
    const prevPage = () => setCurrentPage(p => Math.max(p - 1, 1));

    if (!contrato) {
        return (
            <div className="content-panel">
                <div className="panel-state-wrapper">
                    <FileText size={48} className="text-gray-400" />
                    <p>Nenhum contrato selecionado para visualização</p>
                    <button onClick={onClose} className="action-button action-button-secondary mt-4">
                        <ArrowLeft size={16} />
                        Voltar para Lista
                    </button>
                </div>
            </div>
        );
    }

    const semConteudo = !contrato.conteudoHtml;

    return (
        <div className="content-panel">
            <div className="form-header">
                <div className="preview-title">
                    <FileText size={20} />
                    <div>
                        <h3 className="form-title">Contrato: {contrato.numeroContrato}</h3>
                        <span className="template-name">
                            {typeof contrato.template === 'object' ? contrato.template?.nome : ''}
                        </span>
                    </div>
                </div>
                <button type="button" onClick={onClose} title="Fechar" className="form-close-button">
                    <XCircle size={24} />
                </button>
            </div>

            <fieldset className="form-section">
                <legend className="form-section-title">Resumo do Contrato</legend>
                <div className="summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div className="form-group">
                        <label className="form-label">{contrato.modeloContraparte || 'Contraparte'}:</label>
                        <div className="form-input form-input-disabled">{getContraparteNome(contrato)}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Tipo:</label>
                        <div className="form-input form-input-disabled">{typeof contrato.tipoContrato === 'object' ? contrato.tipoContrato?.nome : 'N/A'}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Status:</label>
                        <div className="form-input form-input-disabled">
                            <span className={`badge ${getStatusClass(contrato.statusContrato)}`}>
                                {contrato.statusContrato || 'N/A'}
                            </span>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Valor Total:</label>
                        <div className="form-input form-input-disabled highlight">
                            {formatCurrency(contrato.valorTotalContrato || 0)}
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Páginas:</label>
                        <div className="form-input form-input-disabled highlight">{totalPages}</div>
                    </div>
                </div>
            </fieldset>

            <fieldset className="form-section">
                <legend className="form-section-title">Controles de Visualização</legend>
                <div className="preview-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button onClick={onClose} className="action-button action-button-secondary" title="Voltar para Lista">
                        <ArrowLeft size={16} />
                        Voltar
                    </button>
                    <button onClick={togglePreview} className="action-button action-button-secondary" title={showPreview ? 'Ocultar Visualização' : 'Mostrar Visualização'}>
                        {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
                    </button>
                    <button onClick={handleDownload} className="action-button action-button-secondary" title="Download HTML" disabled={semConteudo}>
                        <Download size={16} />
                        Download
                    </button>
                    <button onClick={handlePrint} className="action-button action-button-primary" title="Imprimir ou Salvar como PDF" disabled={semConteudo}>
                        <Printer size={16} />
                        Imprimir/PDF
                    </button>
                </div>
            </fieldset>

            {(isLoadingVersoes || versoes.length > 0) && (
                <fieldset className="form-section">
                    <legend className="form-section-title">
                        <History size={16} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
                        Versões do Documento Assinado
                    </legend>
                    {isLoadingVersoes ? (
                        <div className="table-loading-state">
                            <Loader2 size={20} className="animate-spin" />
                        </div>
                    ) : (
                        <p className="text-muted" style={{ marginBottom: '0.5rem' }}>
                            Um PDF novo é gerado a cada assinatura/confirmação de retirada — as versões anteriores continuam disponíveis abaixo.
                        </p>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {versoes.map((v, idx) => (
                            <a
                                key={v._id}
                                href={v.arquivo}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="action-button action-button-secondary"
                                style={{ justifyContent: 'space-between', textDecoration: 'none' }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <FileDown size={16} />
                                    Versão {versoes.length - idx} — {new Date(v.createdAt).toLocaleString('pt-BR')}
                                </span>
                            </a>
                        ))}
                    </div>
                </fieldset>
            )}

            {showPreview && (
                <fieldset className="form-section">
                    <legend className="form-section-title">
                        Preview do Contrato - Página {currentPage} de {totalPages}
                    </legend>

                    <div className="preview-content-wrapper">
                        {semConteudo ? (
                            <div className="panel-state-wrapper">
                                <AlertCircle size={48} className="text-yellow-500" />
                                <p>Este contrato ainda não tem conteúdo gerado.</p>
                            </div>
                        ) : (
                            <div className="a4-preview-wrapper">
                                <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                                    <button onClick={prevPage} disabled={currentPage === 1} className="action-button action-button-secondary">
                                        <ChevronLeft size={16} />
                                        Anterior
                                    </button>
                                    <span className="pagination-info">Página {currentPage} de {totalPages}</span>
                                    <button onClick={nextPage} disabled={currentPage === totalPages} className="action-button action-button-secondary">
                                        Próxima
                                        <ChevronRight size={16} />
                                    </button>
                                </div>

                                <div className="single-page-container">
                                    <div className="a4-page-single">
                                        <iframe
                                            srcDoc={createPageContent(currentPage)}
                                            title={`Contrato: ${contrato.numeroContrato} - Página ${currentPage}`}
                                            className="preview-iframe-single"
                                            sandbox="allow-same-origin"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </fieldset>
            )}
        </div>
    );
};

const getStatusClass = (status) => {
    switch (status) {
        case 'Ativo':
            return 'badge-green';
        case 'Em Elaboração':
            return 'badge-yellow';
        case 'Finalizado':
            return 'badge-gray';
        case 'Cancelado':
            return 'badge-red';
        default:
            return 'badge-gray';
    }
};

export default ContratoPreview;
